import React, { useEffect, useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";

import {
  Spin,
  Tooltip
} from '@lockerpm/design';

import {
  InfoCircleOutlined
} from "@ant-design/icons";

import {
  green,
  orange,
  gray
} from '@ant-design/colors';

import PowerIcon from '../../../assets/images/icons/power.svg';
import UnderVerificationIcon from '../../../assets/images/icons/under-verification.svg';
import RewardSentIcon from '../../../assets/images/icons/reward-sent.svg';

import commonComponents from "../../../components/common";
import rewardComponents from "./components";
import rewardModalComponents from "./components/modals";

import global from "../../../config/global";

import rewardServices from "../../../services/reward";

const Rewards = () => {
  const { t } = useTranslation();
  const {
    PageHeader
  } = commonComponents;
  const {
    GenerateCode,
    Referral,
    ExtInstallation,
    DesktopInstallation,
    AppStoreRating,
    GooglePlayRating,
    CapterraRating,
    ClutchRating,
    G2Rating
  } = rewardComponents;
  const {
    GetCodeConfirmModal,
    GetCodeSuccessModal,
    ResubmitModal,
    VerifyModal
  } = rewardModalComponents;

  const [loading, setLoading] = useState(false);
  const [callingAPI, setCallingAPI] = useState(false);
  const [claimStatus, setClaimStatus] = useState({});
  const [currentCode, setCurrentCode] = useState({});
  const [missions, setMissions] = useState([]);
  const [modalData, setModalData] = useState();

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [resubmitVisible, setResubmitVisible] = useState(false);
  const [verifyVisible, setVerifyVisible] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const totalPercent = useMemo(() => {
    return claimStatus.total_promo_code_value || 20;
  }, [claimStatus])

  const availablePercent = useMemo(() => {
    return claimStatus.available_promo_code_value || 0;
  }, [claimStatus])

  const usedPercent = useMemo(() => {
    return claimStatus.used_promo_code_value || 0;
  }, [claimStatus])

  const remainPercent = useMemo(() => {
    return totalPercent - usedPercent;
  }, [totalPercent, usedPercent])

  const promoCodes = useMemo(() => {
    return claimStatus.generated_available_promo_codes || [];
  }, [claimStatus])

  const gennedPercent = useMemo(() => {
    return promoCodes.map(c => c.value).reduce((a, b) => a + b, 0);
  }, [promoCodes])

  const missionSteps = useMemo(() => {
    return [
      {
        key: 1,
        value: 'not_started',
        icon: PowerIcon,
        color: gray.primary,
        status: 'wait'
      },
      {
        key: 2,
        value: 'under_verification',
        icon: UnderVerificationIcon,
        color: orange.primary,
        status: 'wait'
      },
      {
        key: 3,
        value: 'reward_sent',
        icon: RewardSentIcon,
        color: green.primary,
        status: 'wait'
      }
    ]
  }, [])

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      rewardServices.get_claim(),
      rewardServices.get_missions()
    ]).then(([claimStatusRes, missionsRes]) => {
      setClaimStatus(claimStatusRes);
      setMissions(missionsRes);
    }).catch(() => {
      setClaimStatus({});
      setMissions([])
    })
    setLoading(false);
  }

  const handleGetCode = async () => {
    setConfirmVisible(false);
    setCallingAPI(true);
    await rewardServices.get_code().then((res) => {
      setCurrentCode(res);
      setSuccessVisible(true);
      fetchData();
    }).catch((error) => {
      setCurrentCode({});
      setSuccessVisible(false)
      global.pushError(error)
    })
    setCallingAPI(false);
  }

  const redeemCode = (code) => {
    global.navigate(global.keys.MANAGE_PLANS, {}, { code: code })
  }

  const getMissionById = (id) => {
    return (missions || []).find(m => m.mission.id === id) || {}
  }

  const handleVerify = (data) => {
    fetchData();
    setModalData(data);
    setVerifyVisible(true);
  }

  const handleResubmit = (data) => {
    fetchData();
    setModalData(data);
    setResubmitVisible(true);
  }

  return (
    <div className="rewards layout-content">
      <PageHeader
        title={t('rewards.title')}
        description={<div>
          <p>
            <Trans
              i18nKey={'rewards.subtitle'}
              components={{
                b: <b />
              }}
            />
          </p>
          <p className="mt-4">
            <Tooltip
              className="mr-1 cursor-pointer"
              overlayClassName="min-w-[300px]"
              title={<div>
                <p>{t('rewards.review_description')}</p>
                <p className="mt-3">{t('rewards.review_description1')}</p>
              </div>}
            >
              <InfoCircleOutlined className="font-semibold text-warning"/>
            </Tooltip>
            <i>{t('rewards.review_note')}</i>
          </p>
        </div>}
        actions={[]}
      />
      <Spin spinning={loading}>
        <GenerateCode
          className="my-6"
          availablePercent={availablePercent}
          totalPercent={totalPercent}
          remainPercent={remainPercent}
          gennedPercent={gennedPercent}
          getCode={() => setConfirmVisible(true)}
          redeemCode={redeemCode}
        />
        <Referral className="mb-6"/>
        <ExtInstallation
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('extension_installation_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <DesktopInstallation
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('desktop_app_installation')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <AppStoreRating
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('app_store_rating_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <GooglePlayRating
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('google_play_rating_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <CapterraRating
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('capterra_rating_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <ClutchRating
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('clutch_rating_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
        <G2Rating
          className="mb-6"
          missionSteps={missionSteps}
          mission={getMissionById('g2_rating_and_review')}
          verify={handleVerify}
          resubmit={handleResubmit}
        />
      </Spin>
      <GetCodeConfirmModal
        visible={confirmVisible}
        availablePercent={availablePercent}
        remainPercent={remainPercent}
        callingAPI={callingAPI}
        onClose={() => setConfirmVisible(false)}
        getCode={() => handleGetCode()}
      />
      <GetCodeSuccessModal
        visible={successVisible}
        currentCode={currentCode}
        onClose={() => setSuccessVisible(false)}
        redeemCode={redeemCode}
      />
      <ResubmitModal
        visible={resubmitVisible}
        modalData={modalData}
        onClose={() => setResubmitVisible(false)}
      />
      <VerifyModal
        visible={verifyVisible}
        modalData={modalData}
        availablePercent={availablePercent}
        remainPercent={remainPercent}
        onClose={() => setVerifyVisible(false)}
        onGetCode={() => handleGetCode()}
      />
    </div>
  );
}

export default Rewards;